import { IconOtherQuestionnaire1 } from '@doltech/icons/IconOtherQuestionnaire1';
import { colorsV2 } from '@doltech/ui/lib/figma/colors-v2';
import { Typography } from '@doltech/ui/lib/figma/DesignSystemV2/Typography';
import { SkillEnum, TestTypeEnum } from '@doltech/utils/lib/constants';
import { Progress } from 'antd';
import cl from 'classnames';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import styled from 'styled-components';

import { useTestSubmissionStore } from '../../../../model/test-submissions.hook';

export const DO_TEST_MOBILE_FOOTER_HEIGHT = 70;

const Main = styled.div`
  height: ${DO_TEST_MOBILE_FOOTER_HEIGHT}px;
  max-width: 100vw;
  background: ${colorsV2.white100};
  overflow-x: auto;
  overflow-y: hidden;
  box-shadow: inset 0 1px 0 ${colorsV2.gray10};
  z-index: 100;

  .flex-container {
    width: 100%;
    display: inline-flex;
    align-items: center;
    flex-wrap: nowrap;
    -webkit-overflow-scrolling: touch;
    -ms-overflow-style: -ms-autohiding-scrollbar;
    overflow: hidden;
    padding: 8px 20px 5px;
    justify-content: center;
    &:-webkit-scrollbar {
      display: none;
    }
  }
  .btn-section {
    margin-right: 20px;
    display: flex;
    flex-direction: column;
    align-items: center;
    flex: 0 0 auto;
  }
  .btn-review-submit {
    display: flex;
    flex-direction: column;
    align-items: center;
    flex: 0 0 auto;
    padding-left: 0;
    padding-right: 0;
  }
  .reading-text {
    font-size: 10px;
    white-space: nowrap;
    font-weight: 600;
  }
  .sub-text {
    font-size: 10px;
    white-space: nowrap;
    font-weight: 400;
  }
`;

const shouldDisableReviewButton = ({ isPracticeTest, isReadingTest, isReviewing }) => {
  return !(isPracticeTest || isReadingTest || isReviewing);
};

export const DoTestFooter = observer(({ showOverall, onNavigation }: any) => {
  const testSubmissionStore = useTestSubmissionStore();
  const { currentSectionIndex, isReviewing } = testSubmissionStore;

  const isListeningTest = testSubmissionStore.skill === SkillEnum.LISTENING;
  const isReadingTest = testSubmissionStore.skill === SkillEnum.READING;
  const isPracticeTest = testSubmissionStore.testType === TestTypeEnum.PRACTICE_TEST;

  const isReviewBtnDisabled = shouldDisableReviewButton({
    isPracticeTest,
    isReadingTest,
    isReviewing,
  });

  let sectionPrefix = 'Passage';
  if (isListeningTest) {
    sectionPrefix = 'Section';
  }

  const renderButtonTestSection = (testSection, isActive, index, iconColor, textColor) => {
    return (
      <div
        aria-hidden="true"
        role="button"
        key={index}
        className={cl('btn-section', 'dol-progress-bar', {
          active: isActive,
        })}
        onClick={() => {
          onNavigation?.();

          testSubmissionStore.goToSectionIndex(index, false);
        }}
      >
        <Progress
          key={index}
          strokeWidth={7.5}
          strokeColor={isActive ? colorsV2.primary100 : colorsV2.gray20}
          type="circle"
          format={() => (
            <Typography.Text variant="semi-bold/10" style={{ color: iconColor }}>
              {`${testSection.totalAnswerInGroup}/${testSection.totalQuestionInGroup}`}
            </Typography.Text>
          )}
          width={40}
          trailColor={colorsV2.gray20}
          percent={testSection.percent}
        />
        <Typography.Text variant="semi-bold/10" style={{ color: textColor }}>
          {sectionPrefix} {index + 1}
        </Typography.Text>
      </div>
    );
  };

  return (
    <Main className="do-test-footer layout-footer">
      <div className="flex-container">
        {testSubmissionStore.testSections.map((testSection, index) => {
          let isActive = index === currentSectionIndex;
          const isTourGuideStep2 = testSubmissionStore.tourGuide.tourIndex === 1 && index === 0;

          if (isListeningTest) {
            isActive = (isActive && !isReviewing) || isTourGuideStep2;
          }

          if (isReadingTest) {
            isActive = isActive && !isReviewing;
          }

          const iconColor = isActive ? colorsV2.primary100 : colorsV2.gray100;
          const textColor = isActive ? colorsV2.primary100 : colorsV2.black70;

          return renderButtonTestSection(testSection, isActive, index, iconColor, textColor);
        })}

        <TransparentButton
          onClick={() => {
            if (testSubmissionStore.preventUserAction()) return;
            showOverall();
          }}
          className="btn-review-submit"
        >
          <Progress
            strokeWidth={7.5}
            type="circle"
            format={() => (
              <IconOtherQuestionnaire1
                color={isReviewBtnDisabled ? colorsV2.gray60 : colorsV2.blue100}
              />
            )}
            width={40}
            trailColor={isReviewBtnDisabled ? colorsV2.gray20 : colorsV2.blue100}
            percent={0}
          />
          <Typography.Paragraph
            variant="semi-bold/10"
            disabled={isReviewBtnDisabled}
            ellipsis
            style={{
              color: isReviewBtnDisabled ? colorsV2.gray60 : colorsV2.blue100,
              marginBottom: 0,
            }}
          >
            Review & Submit
          </Typography.Paragraph>
        </TransparentButton>
      </div>
    </Main>
  );
});

const TransparentButton = styled.button`
  border: none;
  outline: none;
  background: none;
  display: inline-flex;
  flex-flow: column nowrap;
  align-items: center;
  justify-content: center;
`;
